import type {
  NewsLinkType,
  NewsVersionEventType,
  NewsVersionLanguage,
  NewsVersionStatus,
  NewsVersionType,
  ProfileType,
} from '@/api/generated/v2'


/* =============== ENewsStatus =============== */
/*
* 1. Черновик
* 2. Ожидает взятия в работу
* 3. В работе
* 7. Отвал
* 8. Ожидает опубликования
* 10. Опубликована
* */
export const ENewsStatus = Object.freeze({
  1: 'DRAFT',
  2: 'WAITS_FOR_EDIT',
  3: 'ON_EDIT',
  7: 'DISPOSED',
  8: 'WAITS_FOR_PUBLICATION',
  10: 'PUBLISHED',
} as const satisfies Record<NewsVersionStatus, string>)

export type ENewsStatusValues = typeof ENewsStatus[keyof typeof ENewsStatus]

/*
* 1. Черновик
* 2. Ожидает взятия в работу
* 3. В работе
* 7. Отвал
* 8. Ожидает опубликования
* 10. Опубликована
* */
export const ENewsStatusReverse = Object.freeze({
  DRAFT: 1,
  WAITS_FOR_EDIT: 2,
  ON_EDIT: 3,
  DISPOSED: 7,
  WAITS_FOR_PUBLICATION: 8,
  PUBLISHED: 10,
} as const satisfies Record<ENewsStatusValues, NewsVersionStatus>)

export const ENewsStatusLabel = Object.freeze({
  1: 'Черновик',
  2: 'Ожидает взятия в работу',
  3: 'В работе',
  7: 'Отвал',
  8: 'Ожидает опубликования',
  10: 'Опубликована',
} as const satisfies Record<NewsVersionStatus, string>)

/* =============== ENewsLink =============== */
/*
* 1. Переопубликование
* 2. Переопубликование без редактирования
* 3. Аннулирование
* 4. Расширение
* 5. Перевод
* 6. Копия
* */
export const ENewsLink = Object.freeze({
  1: 'REPUBLICATION',
  2: 'REPUBLICATION_UNEDITED',
  3: 'REVOCATION',
  4: 'EXTENSION',
  5: 'TRANSLATION',
  6: 'COPY',
} as const satisfies Record<NewsLinkType, string>)

export type ENewsLinkValues = typeof ENewsLink[keyof typeof ENewsLink]

/*
* 1. Переопубликование
* 2. Переопубликование без редактирования
* 3. Аннулирование
* 4. Расширение
* 5. Перевод
* 6. Копия
* */
export const ENewsLinkReverse = Object.freeze({
  REPUBLICATION: 1,
  REPUBLICATION_UNEDITED: 2,
  REVOCATION: 3,
  EXTENSION: 4,
  TRANSLATION: 5,
  COPY: 6,
} as const satisfies Record<ENewsLinkValues, NewsLinkType>)

/* =============== ENewsType =============== */
/*
* 1. Молния
* 2. Экспресс
* 3. Новость
* 4. Выпуск
* 5. Анонс
* 6. Пресс-релиз
* 7. Сводка событий
* 8. Обзор
* 9. Комментарий
* 10. Служебное сообщение
* */
export const ENewsType = Object.freeze({
  1: 'FLASH',
  2: 'EXPRESS',
  3: 'NEWS',
  4: 'ISSUE',
  5: 'ANNOUNCEMENT',
  6: 'PRESS_RELEASE',
  7: 'SUMMARY',
  8: 'REVIEW',
  9: 'COMMENT',
  10: 'SERVICE_MESSAGE',
} as const satisfies Record<NewsVersionType, string>)

export type ENewsTypeValues = typeof ENewsType[keyof typeof ENewsType]

/*
* 1. Молния
* 2. Экспресс
* 3. Новость
* 4. Выпуск
* 5. Анонс
* 6. Пресс-релиз
* 7. Сводка событий
* 8. Обзор
* 9. Комментарий
* 10. Служебное сообщение
* */
export const ENewsTypeReverse = Object.freeze({
  FLASH: 1,
  EXPRESS: 2,
  NEWS: 3,
  ISSUE: 4,
  ANNOUNCEMENT: 5,
  PRESS_RELEASE: 6,
  SUMMARY: 7,
  REVIEW: 8,
  COMMENT: 9,
  SERVICE_MESSAGE: 10,
} as const satisfies Record<ENewsTypeValues, NewsVersionType>)

export const ENewsTypeLabel = Object.freeze({
  1: 'Молния',
  2: 'Экспресс',
  3: 'Новость',
  4: 'Выпуск',
  5: 'Анонс',
  6: 'Пресс-релиз',
  7: 'Сводка событий',
  8: 'Обзор',
  9: 'Комментарий',
  10: 'Служебное сообщение',
} as const satisfies Record<NewsVersionType, string>)

/* =============== ENewsEventType =============== */
/*
*  1. Создана
*  2. Отправлена
*  3. Взята в работу
*  7. Отправлена в отвал
*  8. Запланирована
*  9. Отменена
*  10. Опубликована
*  11. Переопубликована
*  12. Переопубликована без редактирования
*  13. Аннулирована
*  14. Расширена
*  15. Переведена
*  16. Отозвана
*  100. Прочитана
*  200. Изменена
*  201. Удалена
*  202. Перепланирована
*  300. Добавлены авторы
*  301. Удалены авторы
*  400. Добавлены коды
*  401. Удалены коды
*  402. Изменены коды направлений публикации
*  403. Коды проставлены автоматически
*  500. Отправлена на ящик АСОИ
*  501. Продублирована в АСОИ
*  600. Добавлен комментарий
*  601. Прочитан комментарий
*  700. Добавлены связи
*  701. Удалены связи
* */
export const ENewsEventType = Object.freeze({
  1: 'CREATED',
  2: 'SENT_FOR_EDIT',
  3: 'PICKED_FOR_EDIT',
  7: 'DISPOSED',
  8: 'SCHEDULED',
  9: 'CANCELLED',
  10: 'PUBLISHED',
  11: 'REPUBLISHED',
  12: 'REPUBLISHED_UNEDITED',
  13: 'REVOKED',
  14: 'EXTENDED',
  15: 'TRANSLATED',
  16: 'RECALLED',
  100: 'READ',
  200: 'UPDATED',
  201: 'DELETED',
  202: 'RESCHEDULED',
  300: 'AUTHORS_ADDED',
  301: 'AUTHORS_DELETED',
  400: 'CODES_ADDED',
  401: 'CODES_DELETED',
  402: 'ROUTE_CODES_UPDATED',
  403: 'CODES_AUTO_ASSIGNED',
  500: 'SENT_TO_ASOI',
  501: 'DUPLICATED_IN_ASOI',
  600: 'COMMENT_ADDED',
  601: 'COMMENT_READ',
  700: 'LINKS_ADDED',
  701: 'LINKS_DELETED',
} as const satisfies Record<NewsVersionEventType, string>)

export type ENewsEventTypeValues = typeof ENewsEventType[keyof typeof ENewsEventType]

/*
*  1. Создана
*  2. Отправлена
*  3. Взята в работу
*  7. Отправлена в отвал
*  8. Запланирована
*  9. Отменена
*  10. Опубликована
*  11. Переопубликована
*  12. Переопубликована без редактирования
*  13. Аннулирована
*  14. Расширена
*  15. Переведена
*  16. Отозвана
*  100. Прочитана
*  200. Изменена
*  201. Удалена
*  202. Перепланирована
*  300. Добавлены авторы
*  301. Удалены авторы
*  400. Добавлены коды
*  401. Удалены коды
*  402. Изменены коды направлений публикации
*  403. Коды проставлены автоматически
*  500. Отправлена на ящик АСОИ
*  501. Продублирована в АСОИ
*  600. Добавлен комментарий
*  601. Прочитан комментарий
*  700. Добавлены связи
*  701. Удалены связи
* */
export const ENewsEventTypeReverse = Object.freeze({
  CREATED: 1,
  SENT_FOR_EDIT: 2,
  PICKED_FOR_EDIT: 3,
  DISPOSED: 7,
  SCHEDULED: 8,
  CANCELLED: 9,
  PUBLISHED: 10,
  REPUBLISHED: 11,
  REPUBLISHED_UNEDITED: 12,
  REVOKED: 13,
  EXTENDED: 14,
  TRANSLATED: 15,
  RECALLED: 16,
  READ: 100,
  UPDATED: 200,
  DELETED: 201,
  RESCHEDULED: 202,
  AUTHORS_ADDED: 300,
  AUTHORS_DELETED: 301,
  CODES_ADDED: 400,
  CODES_DELETED: 401,
  ROUTE_CODES_UPDATED: 402,
  CODES_AUTO_ASSIGNED: 403,
  SENT_TO_ASOI: 500,
  DUPLICATED_IN_ASOI: 501,
  COMMENT_ADDED: 600,
  COMMENT_READ: 601,
  LINKS_ADDED: 700,
  LINKS_DELETED: 701,
} as const satisfies Record<ENewsEventTypeValues, NewsVersionEventType>)

export const ENewsEventTypeLabel = Object.freeze({
  1: 'Создана',
  2: 'Отправлена',
  3: 'Взята в работу',
  7: 'Отправлена в отвал',
  8: 'Запланирована',
  9: 'Отменена',
  10: 'Опубликована',
  11: 'Переопубликована',
  12: 'Переопубликована без редактирования',
  13: 'Аннулирована',
  14: 'Расширена',
  15: 'Переведена',
  16: 'Отозвана',
  100: 'Прочитана',
  200: 'Изменена',
  201: 'Удалена',
  202: 'Перепланирована',
  300: 'Добавлены авторы',
  301: 'Удалены авторы',
  400: 'Добавлены коды',
  401: 'Удалены коды',
  402: 'Изменены коды направлений публикации',
  403: 'Коды проставлены автоматически',
  500: 'Отправлена на ящик АСОИ',
  501: 'Продублирована в АСОИ',
  600: 'Добавлен комментарий',
  601: 'Прочитан комментарий',
  700: 'Добавлены связи',
  701: 'Удалены связи',
} as const satisfies Record<NewsVersionEventType, string>)

/* =============== ELanguage =============== */
/*
* 1. Русский
* 2. Английский
* */
export const ELanguage = Object.freeze({
  1: 'RU',
  2: 'EN',
} as const satisfies Record<NewsVersionLanguage, string>)

export type ELanguageValues = typeof ELanguage[keyof typeof ELanguage]

export const ELanguageReverse = Object.freeze({
  RU: 1,
  EN: 2,
} as const satisfies Record<ELanguageValues, NewsVersionLanguage>)

/* =============== EProfileType =============== */
/*
* 1. Для написания новости или периодического издания
* 2. Для написания новости или периодического издания
* 3. Для опубликования периодического издания
* 4. По умолчанию
* */
export const EProfileType = Object.freeze({
  1: 'NEWS_OR_ISSUE',
  2: 'PUBLISH_NEWS',
  3: 'PUBLISH_ISSUE',
  4: 'DEFAULT',
} as const satisfies Record<ProfileType, string>)

export type EProfileTypeValues = typeof EProfileType[keyof typeof EProfileType]
